import React from 'react';
import { Helmet } from 'react-helmet';
import CalendarComponent from './CalendarComponent';
import BusinessInfoForm from './BusinessInfoForm';
import LogoBookMe from './LogoBookMe';
import { useLandingSettings } from '../hooks/useLandingSettings';

const BookMeLandingPage = () => {

  const handleWhatsappClick = () => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'whatsapp_click', {
        event_category: 'engagement',
        event_label: 'BookMe WhatsApp CTA',
      });
    }
  };
  const { settings: landing } = useLandingSettings();
  const hero = landing.hero || {};
  const whatsappNumber = landing.whatsapp?.number || '15558364840';
  const whatsappMessage = landing.whatsapp?.message || 'Hola, tengo preguntas sobre el sistema Book Me';
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <div className="bg-white text-gray-900 dark:bg-gray-900 dark:text-white">
      <Helmet>
        <title>Book Me | CRM y sistema de reservas a la medida — BBTS</title>
        <meta
          name="description"
          content="Book Me es el CRM que BBTS construye a la medida del flujo real de tu negocio: agenda, ventas, pagos y equipo en un solo sistema. Casos reales en Puerto Rico."
        />
      </Helmet>

<header className="w-full px-6 py-4 flex justify-start items-center bg-white dark:bg-gray-900 shadow-sm">
  <LogoBookMe />
</header>


      {/* HERO */}
      <section className="pt-20 pb-16 text-center bg-gradient-to-br from-orange-50 to-white dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-5xl font-extrabold mb-4 leading-tight">
            {hero.title}{' '}
            {hero.highlight && <span className="text-orange-500">{hero.highlight}</span>}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
            {hero.description}
          </p>

          {/* CTA */}
          <div className="flex justify-center mt-6">
            <a href={hero.ctaLink || '#lead-form'} className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-6 rounded-lg transition">
              {hero.ctaText || '¿Qué necesitas para tu negocio?'}
            </a>
          </div>

          <div className="mt-12">
            <CalendarComponent />
          </div>
        </div>
      </section>

      {/* FUNCIONALIDADES */}
      <section className="bg-gray-50 dark:bg-gray-800 py-20">
        <div className="max-w-screen-xl mx-auto px-4">
          <h2 className="text-3xl font-extrabold text-center mb-12">Funcionalidades clave</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {landing.features.map((item) => (
              <div key={item.id || item.title} className="bg-white rounded-lg overflow-hidden shadow-md dark:bg-gray-900">
                {item.mediaType === 'video' ? (
                  <video
                    src={item.media}
                    className="h-48 w-full object-cover"
                    autoPlay
                    loop
                    muted
                    playsInline
                  />
                ) : (
                  <img
                  src={item.media}
                  alt={item.title}
                  className="h-64 w-full object-cover"
                  loading="lazy"
                />
                
                )}
                <div className="p-5">
                  <h3 className="text-lg font-bold text-orange-500">{item.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400">{item.desc}</p>
                </div>
              </div>
            ))}
            
          </div>
        </div>
      </section>

      {/* CÓMO SE ADAPTA A TU INDUSTRIA */}
      <section className="bg-white dark:bg-gray-900 py-20">
        <div className="max-w-screen-xl mx-auto px-4">
          <h2 className="text-3xl font-extrabold text-center mb-4">Cómo se adapta a tu industria</h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            El mismo sistema, configurado al proceso real de cada negocio — no una plantilla fija.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {(landing.industries || []).map((item) => (
              <div key={item.id} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 shadow-md">
                <h3 className="text-lg font-bold text-orange-500 mb-2">{item.title}</h3>
                <p className="text-gray-600 dark:text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CASOS REALES */}
      <section className="bg-gray-50 dark:bg-gray-800 py-20">
        <div className="max-w-screen-xl mx-auto px-4">
          <h2 className="text-3xl font-extrabold text-center mb-4">Casos reales</h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Negocios reales corriendo hoy sobre el mismo sistema.
          </p>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {(landing.caseStudies || []).map((item) => (
              <div key={item.id} className="bg-white dark:bg-gray-900 rounded-lg overflow-hidden shadow-md">
                {item.media?.[0] && (
                  <img
                    src={item.media[0]}
                    alt={item.name}
                    className="h-56 w-full object-cover"
                    loading="lazy"
                  />
                )}
                <div className="p-6">
                  <h3 className="text-lg font-bold text-orange-500">{item.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{item.sector}</p>
                  <p className="text-gray-600 dark:text-gray-300">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIOS */}
      <section className="bg-white dark:bg-gray-900 py-16">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-10">Lo que dicen nuestros clientes</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {landing.testimonials.map((t, i) => (
              <div key={`${t.name}-${i}`} className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <p className="italic text-gray-700 dark:text-gray-300 mb-4">"{t.text}"</p>
                <p className="font-semibold text-orange-500">{t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

     {/* VISTA DEL SISTEMA */}
<section className="bg-white dark:bg-gray-900 py-20">
  <div className="max-w-6xl mx-auto px-4 text-center">
    <h2 className="text-3xl font-bold mb-4">Tu sistema se ve así</h2>
    <p className="text-lg text-gray-600 dark:text-gray-400 mb-12">
      Panel moderno, adaptable a cualquier dispositivo.
    </p>

    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
      {landing.gallery.map((image, idx) => (
        <img
          key={`${image}-${idx}`}
          src={image}
          alt={`Vista ${idx + 1}`}
          className="rounded-xl shadow-lg border w-full object-cover h-64"
          loading="lazy"
        />
      ))}
    </div>
  </div>
</section>

      {/* FORMULARIO INFO DE NEGOCIO */}
      <section id="lead-form" className="bg-gray-50 dark:bg-gray-800 py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-6">Personaliza tu sistema ideal</h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 text-center mb-10">
            Cuéntanos sobre tu negocio y agenda una llamada gratuita. Sin compromiso, sin tarjeta.
          </p>
          <BusinessInfoForm />
        </div>
      </section>
     {/* Botón flotante: WhatsApp con texto e ícono */}
     <a
  href={whatsappLink}
  target="_blank"
  rel="noopener noreferrer"
  onClick={handleWhatsappClick}
  className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-lg transition-all duration-300 ease-in-out max-w-xs w-full sm:w-auto"
  aria-label="Habla con un representante por WhatsApp"
>
  {/* Ícono: Representante con headset */}
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-6 h-6 flex-shrink-0"
    fill="currentColor"
    viewBox="0 0 24 24"
  >
    <path d="M12 2C6.48 2 2 6.48 2 12v1a3 3 0 003 3h1v-5H5a5 5 0 0110 0h2a7 7 0 00-7-7zm6 8h-2v5a2 2 0 01-2 2h-1v3h4a3 3 0 003-3v-5a3 3 0 00-2-2z" />
  </svg>

  {/* Texto corto en móvil, largo en sm+ */}
  <span className="font-semibold text-sm">
    <span className="sm:hidden">{landing.whatsapp?.labelMobile || 'Hablar con representante'}</span>
    <span className="hidden sm:inline">{landing.whatsapp?.labelDesktop || '¿Tienes preguntas? Habla con un representante ahora'}</span>
  </span>
</a>


      {/* FOOTER */}
      <footer className="bg-white dark:bg-gray-900 py-6 text-center border-t border-gray-200 dark:border-gray-700">
  <p className="text-sm text-gray-500 dark:text-gray-400">
    © 2025 Book Me. Todos los derechos reservados.{' '}
    <a
      href="https://bbtspr.com"
      target="_blank"
      rel="noopener noreferrer"
      className="text-orange-500 hover:text-orange-600 font-medium"
    >
      Powered by bbtspr.com
    </a>
  </p>
</footer>

    </div>
  );
};

export default BookMeLandingPage;
